import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { GENDER_MAP, SYSTEM_PATH, WORK_FORM_MAP, MSG } from '../../core/configs/constants';
import { useStore } from '../../core/utils/hook';
import CourseListByStudent from './components/CourseListByStudent';
import { ReactNotifications } from '../../components';

const StudentDetailScreen = observer(props => {

    // params
    const { id } = useParams();
    const navigate = useNavigate();

    // store
    const { 
        groupAdminStore: { studentDetail, getStudentDetail, updateStudentEmployeeCode }
    } = useStore();

    const [headerHeight, setHeaderHeight] = useState(0);
    const [activeTab, setActiveTab] = useState('registration'); // 'registration' or 'history'
    const [employeeCode, setEmployeeCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);

    const isUseEmployeeCode = !!studentDetail?.useEmployeeCode;
    const isEnableDepartmentHierarchy = !!studentDetail?.enableDepartmentHierarchy;

    useEffect(() => {
        const header = document.getElementById('header-site');
        if (!header) return;

        const updateHeight = () => {
            setHeaderHeight(header.offsetHeight);
        };

        // Follow initial height header
        updateHeight();

        // Follow resize header
        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(header);

        // Follow resize window
        window.addEventListener('resize', updateHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, []);

    // lifecycle
    useEffect(() => {
        getStudentDetail(id);
    }, [id]);

    useEffect(() => {
        if (studentDetail) {
            setEmployeeCode(studentDetail.employeeCode || '');
            setErrorMessage('');
        }
    }, [studentDetail]);

    const handleBack = () => {
        navigate(SYSTEM_PATH.STUDENT_GROUP_LIST);
    };

    const handleEmployeeCodeChange = (e) => {
        const val = e.target.value;
        setEmployeeCode(val);
        const trimmed = val.trim();
        if (trimmed && trimmed.length > 255) {
            setErrorMessage(MSG['error.max_length'](255));
        } else if (trimmed && !/^[A-Za-z0-9]+$/.test(trimmed)) {
            setErrorMessage(MSG['error.employee_code_format']);
        } else {
            setErrorMessage('');
        }
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        const trimmedCode = (employeeCode || '').trim();
        if (trimmedCode && trimmedCode.length > 255) {
            setErrorMessage(MSG['error.max_length'](255));
            return;
        }
        if (trimmedCode && !/^[A-Za-z0-9]+$/.test(trimmedCode)) {
            setErrorMessage(MSG['error.employee_code_format']);
            return;
        }
        setIsSubmitting(true);
        const res = await updateStudentEmployeeCode(id, trimmedCode);
        if (res.ok) {
            ReactNotifications('success', MSG['inform.success.update']);
            setErrorMessage('');
        } else {
            setErrorMessage(res.message || MSG['api.response.server_error']);
        }
        setIsSubmitting(false);
    };

    const hasChanges = employeeCode.trim() !== (studentDetail?.employeeCode || '').trim();

    return (
        <div id='main' className='wrapper main-student-list main-view-student-detail'>
            <div className="bres-cus" 
                style={{ position: 'sticky', zIndex: 50, top: `${headerHeight}px`, left: '0', width: '100%', backgroundColor: '#FFF'}}>
                <div className='container'>
                    <ul>
                        <li>
                            <Link to={SYSTEM_PATH.HOME}><img src='/images/icon-home.png' alt='logo-home'/></Link>
                        </li>
                        <li className='dash'>\</li>
                        <li ><Link style={{textDecoration: 'none'}} to={SYSTEM_PATH.STUDENT_GROUP_LIST}>受講者リスト</Link></li>
                        <li className='dash'>\</li>
                        <li>受講者詳細</li>
                    </ul>
                </div>
            </div>
            
            <section className='content-student-list' style={{ paddingBottom: '0' }}>
                <div className='container'>
                    <h1 className='title-primary'>受講者リスト</h1>
                    
                    <div className="tabs-container">
                        <div 
                            className={`tab-item ${activeTab === 'registration' ? 'active' : ''}`}
                            onClick={() => setActiveTab('registration')}
                        >
                            登録情報
                        </div>
                        <div 
                            className={`tab-item ${activeTab === 'history' ? 'active' : ''}`}
                            onClick={() => setActiveTab('history')}
                        >
                            受講講座履歴
                        </div>
                    </div>
                </div>
            </section>

            {activeTab === 'history' ? (
                <section className='content-student-list' style={{ paddingTop: '0' }}>
                    <div className='container'>
                        <CourseListByStudent />
                    </div>
                </section>
            ) : (
                <section className='top-student-list' style={{ paddingTop: '0' }}>
                    <div className='container'>
                        <div className='content-student-detail'>
                            <div className='item'>
                                <div className='left'>
                                    <span>名前</span>
                                </div>
                                <div className='right'>
                                    <span>{studentDetail?.firstName} {studentDetail?.lastName}</span>
                                </div>
                            </div>
                            <div className='item'>
                                <div className='left'>
                                    <span>名前(フリガナ)</span>
                                </div>
                                <div className='right'>
                                    <span>{studentDetail?.firstNameFuri} {studentDetail?.lastNameFuri}</span>
                                </div>
                            </div>
                            {isUseEmployeeCode &&
                                <div className='item'>
                                    <div className='left'>
                                        <span>社員番号</span>
                                    </div>
                                    <div className='right'>
                                        <input
                                            type="text"
                                            className="employee-code-input"
                                            value={employeeCode}
                                            maxLength={255}
                                            onChange={handleEmployeeCodeChange}
                                        />
                                        {errorMessage && <p className='text-error'>{errorMessage}</p>}
                                    </div>
                                </div>
                            }
                            <div className='item'>
                                <div className='left'>
                                    <span>性別</span>
                                </div>
                                <div className='right'>
                                    <span>{GENDER_MAP[studentDetail?.gender]}</span>
                                </div>
                            </div>
                            <div className='item'>
                                <div className='left'>
                                    <span>メールアドレス</span>
                                </div>
                                <div className='right'>
                                    <span>{studentDetail?.email}</span>
                                </div>
                            </div>
                            <div className='item'>
                                <div className='left'>
                                    <span>薬剤師名簿登録番号</span>
                                </div>
                                <div className='right'>
                                    <span>{studentDetail?.pharmacistNumber}</span>
                                </div>
                            </div>
                            <div className='item'>
                                <div className='left'>
                                    <span>沖/外選択</span>
                                </div>
                                <div className='right'>
                                    <span>{WORK_FORM_MAP[studentDetail?.workForm]}</span>
                                </div>
                            </div>
                            <div className='item'>
                                <div className='left'>
                                    <span>法人名</span>
                                </div>
                                <div className='right'>
                                    <span>{studentDetail?.groupName}</span>
                                </div>
                            </div>
                            {isUseEmployeeCode && isEnableDepartmentHierarchy &&
                                <div className='item'>
                                    <div className='left'>
                                        <span>部署名</span>
                                    </div>
                                    <div className='right'>
                                        <span>{studentDetail?.departmentName || ''}</span>
                                    </div>
                                </div>
                            }
                        </div>
                        <div className="link-button">
                            <button className="btn-1" onClick={handleBack} disabled={isSubmitting}>
                                戻る
                            </button>
                            {/* Submit is only meaningful when the group subscribes to the employee code service */}
                            {isUseEmployeeCode &&
                                <button className="btn-2" onClick={handleSubmit} disabled={isSubmitting || !hasChanges || !!errorMessage}>
                                    {isSubmitting ? '送信中...' : '送信'}
                                </button>
                            }
                        </div>
                    </div>
                </section>
            )}
        </div>
    )
})

export default StudentDetailScreen;